import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Text from 'particles/Text'
import styled from 'particles/themes/styled'
import _ from 'lodash'
import Icon, { IconName } from 'particles/Icon'

export type Size = 's' | 'm'

const readOnlySizeMap: Record<Size, { minHeight: string; paddingY: string; paddingX: string }> = {
    m: {
        minHeight: '40px',
        paddingY: '10px',
        paddingX: '11px',
    },
    s: {
        minHeight: '32px',
        paddingY: '6px',
        paddingX: '7px',
    },
}

interface ReadOnlyContainer {
    width?: number | string
    size: Size
    hasValue: boolean
    minTextAreaHeight?: number
}
const ReadOnlyContainer = styled.div<ReadOnlyContainer>`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[100]};
    width: ${({ width }): string => (width ? (typeof width === 'string' ? width : width + 'px') : '100%')};
    max-width: ${({ width }): string => (width ? (typeof width === 'string' ? width : width + 'px') : '100%')};
    height: fit-content;
    min-height: ${({ size, minTextAreaHeight }) =>
        minTextAreaHeight ? `${minTextAreaHeight}px` : readOnlySizeMap[size].minHeight};
    padding: ${({ size }) => `${readOnlySizeMap[size].paddingY} ${readOnlySizeMap[size].paddingX}`};
    border-left: 1px solid ${({ theme }) => theme.colors.border.primary};
    box-sizing: border-box;
    border-radius: 4px;

    textarea {
        width: 100%;
        max-width: 100%;
        word-break: break-word;
        color: ${({ hasValue, theme }) => (hasValue ? theme.colors.text.primary : theme.colors.text.tertiary)};
        outline: none;
        resize: none;
        font-family: inherit;
        border: none;
        ${({ hasValue, theme }) => (hasValue ? theme.textScale.bodyS700 : theme.textScale.bodyS400)};
    }
    &.card-item {
        padding: 0;
        border: none;
        textarea {
            ${({ theme }) => theme.textScale.bodyS400};
            background: transparent;
        }
    }
`

type ReadOnlyDisplayTypes = 'card-item' | 'regular'
interface ReadOnlyProps {
    className?: string
    value?: string
    width?: number | string
    size?: Size
    displayType?: ReadOnlyDisplayTypes
    minTextAreaHeight?: number
    icon?: IconName
}

export const ReadOnly: React.FC<ReadOnlyProps> = ({
    className,
    value,
    width,
    size = 's',
    displayType = 'regular',
    minTextAreaHeight,
    icon,
}) => {
    const [t] = useTranslation()
    const textAreaRef = useRef<HTMLTextAreaElement>(null)
    useEffect(() => {
        // textareaResize
        if (textAreaRef && textAreaRef.current) {
            textAreaRef.current.style.height = 'auto'
            textAreaRef.current.style.height = textAreaRef.current.scrollHeight + 'px'
        }
    }, [value, width])

    return (
        <ReadOnlyContainer
            size={size}
            width={width}
            className={`${className} ${displayType === 'card-item' ? 'card-item' : ''}`}
            hasValue={Boolean(value)}
            minTextAreaHeight={minTextAreaHeight}
        >
            {icon && <Icon name={icon} size="20px" color="disable" className="input-icon" />}
            <textarea
                readOnly
                value={value ? value : `${t('placeholderText.noDetailsAdded')}`}
                rows={1}
                data-emoji-input="unicode"
                ref={textAreaRef}
            />
        </ReadOnlyContainer>
    )
}

const InputCharacterCounterContainer = styled.div`
    display: flex;
`
interface InputCharacterCounterProps {
    inputCharLength: number
    maxInputLength: number
}

export const InputCharacterCounter: React.FC<InputCharacterCounterProps> = ({ inputCharLength, maxInputLength }) => {
    return (
        <InputCharacterCounterContainer>
            <Text scale="caption400" color="secondary" as="div">
                {inputCharLength}
            </Text>
            <Text scale="caption400" color="secondary" as="div">
                /
            </Text>
            <Text scale="caption400" color="secondary" as="div">
                {maxInputLength}
            </Text>
        </InputCharacterCounterContainer>
    )
}
